import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import {
    IThemeModel,
    IThemeSelectItem,
} from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';
import { OFFICE_THEME_2013, OFFICE_THEME_2023, TENCENT_DOCS_THEME } from './default.config';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import {
    ITemplateFormatService,
} from '@src/slide/application/service/template-format-service/template-format-service.interface';

interface IColorOption {
    color: ITDColor;
    colorNumber: number;
}

// hsl里，色相为0-360循环（色相是一个圈）
const MAX_DUE_VALUE = 360;
const MIN_DUE_VALUE = 0;

// 着色数量，定值，实际就是ooxml里的accent1-6，根据这个数量来均分色相
const ACCENT_NUMBER = 6;

/**
 * 模版格式化服务，职责上关注模版非标准内容的修复
 */
export class TemplateFormatService implements ITemplateFormatService {
    /**
     * 标准化模版
     * @param uiDataService
     */
    public formatTemplateStandard = (uiDataService: IUiDataService) => {
        const { presentation } = uiDataService;
        const { themeList } = presentation;
        let isDefaultTheme = true;

        // 先检查主题是否是默认主题
        themeList.forEach((themeModel) => {
            const flag = this.checkTemplateTheme(themeModel);
            if (!flag) {
                isDefaultTheme = flag;
            }
        });

        // 是默认主题的话，就要去考虑推测模版的主题色意图，非默认模版就可以理解为作者编辑过这个模版
        if (isDefaultTheme) {
            const colorMap = this.collectMainThemeColor(uiDataService.presentation);
            const newThemeList = this.classifyColor(colorMap);
            const colorSchemaProps: any = {};
            newThemeList.forEach((color, index) => {
                colorSchemaProps[`accent${index + 1}`] = {
                    val: STSchemeColorVal[`accent${index + 1}`],
                    color: color.getColorWithNoAlpha().replace('#', ''),
                };
            });
            this.updateThemeWithNewColor(uiDataService, colorSchemaProps);
        }
    };

    /**
     * 检测主题
     * @param themeModel
     */
    public checkTemplateTheme = (themeModel: IThemeModel) => {
        const { clrScheme } = themeModel;
        let isDefaultTheme = true;
        Object.keys(clrScheme).forEach((key) => {
            const office2013 = Object.assign({}, OFFICE_THEME_2013);
            if (clrScheme[key].toLowerCase?.() !== office2013[key].toLowerCase?.()) {
                isDefaultTheme = false;
            }

            // 后续把2023加上
            // if (clrScheme[key] !== OFFICE_THEME_2023[key]) {
            //     isDefaultTheme = false;
            // }

            // const tencent = Object.assign({}, TENCENT_DOCS_THEME);
            // if (clrScheme[key] !== tencent[key]) {
            //     isDefaultTheme = false;
            // }
        });

        return isDefaultTheme;
    };

    /**
     * 收集出现的颜色
     * @param presentation
     */
    public collectMainThemeColor = (presentation: IPresentationModel): Map<string, IColorOption> => {
        const colorMap = new Map<string, IColorOption>();
        presentation.children.forEach((slideModel) => {
            slideModel.children.forEach((element) => {
                switch (element.type) {
                    case UIModelType.SHAPE:
                        this.collectShape(element as IShapeModel, colorMap);
                        break;
                    case UIModelType.GROUP:
                        this.collectGroup(element as IGroupModel, colorMap);
                        break;
                }
            });
        });

        return colorMap;
    };

    /**
     * 颜色分类给主题色
     */
    private classifyColor = (colorMap: Map<string, IColorOption>): ITDColor[] => {
        let maxColorDueDiff = 0;
        let minDue: ITDColor | undefined;
        let maxDue: ITDColor | undefined;

        // 色相是一个0-360的环，色相范围要考虑两种区间。e.g. 50-100、200-360-50
        let isRevert = false;
        const themeTdColorList: ITDColor[] = [];

        // 6个以上颜色，先做合并，将相似的颜色归类到一个语义下
        if (colorMap.size > ACCENT_NUMBER) {
            const colorList: ITDColor[] = [];
            colorMap.forEach((colorOption, colorString) => {
                colorList.push(colorOption.color);
            });
            colorList.sort((a, b) => a.hsl.H - b.hsl.H);

            let lastColor = colorList[0];

            // 收集色相起始点
            for (let index = 1; index < colorList.length; index++) {
                const curColor = colorList[index];

                if (curColor.hsl.H - lastColor.hsl.H > maxColorDueDiff) {
                    minDue = lastColor;
                    maxDue = curColor;
                    maxColorDueDiff = curColor.hsl.H - lastColor.hsl.H;
                }

                lastColor = curColor;

                if (index === colorList.length - 1) {
                    if (MAX_DUE_VALUE - curColor.hsl.H + colorList[0].hsl.H > maxColorDueDiff) {
                        maxDue = lastColor;
                        minDue = colorList[0];
                        isRevert = true;
                    }
                }
            }

            if (!maxDue || !minDue) return themeTdColorList;

            // 将色相均分为6个着色语义
            this.fillThemeColorWithSharingDue(isRevert, maxDue, minDue, themeTdColorList);
        } else { // 6个以下，直接填充主题
            this.fillThemeColorWithLessColor(colorMap, themeTdColorList);
        }

        return themeTdColorList;
    };

    /**
     * 通过分析出来的色相区间，均分这个区间的点来填充主题
     * @param isRevert
     * @param maxDue
     * @param minDue
     * @param themeTdColorList
     * @private
     */
    private fillThemeColorWithSharingDue(
        isRevert: boolean,
        maxDue: ITDColor,
        minDue: ITDColor,
        themeTdColorList: ITDColor[],
    ) {
        if (!isRevert) {
            const area1 = MAX_DUE_VALUE - maxDue!.hsl.H;
            const area2 = minDue!.hsl.H;
            const accentDueLength = (area1 + area2) / ACCENT_NUMBER;

            for (let index = 0; index < ACCENT_NUMBER; index++) {
                const hsl = Object.assign({}, maxDue.hsl);
                hsl.H = hsl.H + (index * accentDueLength);
                if (hsl.H > MAX_DUE_VALUE) {
                    hsl.H = hsl.H - MAX_DUE_VALUE;
                }

                const rgb = TDColor.rgb2RgbString(TDColor.hsl2Rgb(hsl));
                const newTDColor = new TDColor({
                    rgbaStr: rgb,
                });

                themeTdColorList.push(newTDColor);
            }
        } else {
            const area1 = maxDue!.hsl.H - minDue!.hsl.H;
            const accentDueLength = area1 / ACCENT_NUMBER;

            for (let index = 0; index < ACCENT_NUMBER; index++) {
                const hsl = Object.assign({}, minDue.hsl);
                hsl.H = hsl.H + (index * accentDueLength);
                if (hsl.H > MAX_DUE_VALUE) {
                    hsl.H = hsl.H - MAX_DUE_VALUE;
                }

                const rgb = TDColor.rgb2RgbString(TDColor.hsl2Rgb(hsl));
                const newTDColor = new TDColor({
                    rgbaStr: rgb,
                });

                themeTdColorList.push(newTDColor);
            }
        }
    }

    /**
     * 分析出来不足6个颜色的情况下，直接将这几个颜色填充到accent1-6
     * @param colorMap
     * @param themeTdColorList
     * @private
     */
    private fillThemeColorWithLessColor(colorMap: Map<string, IColorOption>, themeTdColorList: ITDColor[]) {
        const colorOptionList: IColorOption[] = [];
        colorMap.forEach((colorOption, colorString) => {
            colorOptionList.push(colorOption);
        });

        colorOptionList.sort((a, b) => b.colorNumber - a.colorNumber);

        themeTdColorList.push(...colorOptionList.map(a => a.color));
    }

    /**
     * 收集组合出现的颜色
     * @param element
     * @param colorMap
     */
    private collectGroup = (element: IGroupModel, colorMap: Map<string, IColorOption>) => {
        element.children.forEach((child) => {
            if (child.type === UIModelType.GROUP) {
                this.collectGroup(child as IGroupModel, colorMap);
            } else if (child.type === UIModelType.SHAPE) {
                this.collectShape(child as IShapeModel, colorMap);
            }
        });
    };

    /**
     * 收集形状的颜色，目前主要是纯色和渐变
     * todo: 图片相对复杂点，要实际渲染出来再根据像素去算
     * @param element
     * @param colorMap
     */
    private collectShape = (element: IShapeModel, colorMap: Map<string, IColorOption>) => {
        const { fillBackground } = element.props.style;
        if (fillBackground) {
            const { solidFill, gradFill } = fillBackground;
            const color = solidFill?.color;
            if (color) {
                this.computeColor(color, colorMap);
            }

            if (gradFill?.gsList) {
                gradFill.gsList.forEach((gs) => {
                    const { color } = gs;
                    if (color) {
                        this.computeColor(color, colorMap);
                    }
                });
            }
        }
    };

    /**
     * 收集比较有"主题意图"的颜色
     * 一般来说黑白颜色更多用于文字、背景语义（bg、tx）
     * 而相对高饱和、中等亮度的文字在多数情况更能体现主题意图
     * 因此在收集颜色时，先做一次过滤
     * @param color
     * @param colorMap
     * @private
     */
    private computeColor(color: ITDColor, colorMap: Map<string, IColorOption>) {
        const LOW_SATURATION_NUMBER = 50;
        const HIGH_LUM_NUMBER = 200;
        const LOW_LUM_NUMBER = 40;
        if (
            color.hsl.S > LOW_SATURATION_NUMBER
            && color.hsl.L < HIGH_LUM_NUMBER
            && color.hsl.L > LOW_LUM_NUMBER
        ) {
            const customColor = color.srgb_color;
            const colorCached = colorMap.get(customColor);
            if (colorCached) {
                colorCached.colorNumber = colorCached.colorNumber + 1;
            } else {
                colorMap.set(customColor, {
                    color,
                    colorNumber: 1,
                });
            }
        }
    }

    /**
     * 用分析出来的颜色更新主题
     * @param uiDataService
     * @param colorSchemaProps
     * @private
     */
    private updateThemeWithNewColor(uiDataService: IUiDataService, colorSchemaProps: any) {
        uiDataService.presentation.themeList.forEach((themeModel: IThemeModel) => {
            const { id, clrScheme } = themeModel;
            const localColorSchema: any = {
                dk1: {
                    val: STSchemeColorVal.dk1,
                    color: clrScheme[STSchemeColorVal.dk1],
                },
                dk2: {
                    val: STSchemeColorVal.dk2,
                    color: clrScheme[STSchemeColorVal.dk2],
                },
                lt1: {
                    val: STSchemeColorVal.lt1,
                    color: clrScheme[STSchemeColorVal.lt1],
                },
                lt2: {
                    val: STSchemeColorVal.lt2,
                    color: clrScheme[STSchemeColorVal.lt2],
                },
                accent1: {
                    val: STSchemeColorVal.accent1,
                    color: clrScheme[STSchemeColorVal.accent1],
                },
                accent2: {
                    val: STSchemeColorVal.accent2,
                    color: clrScheme[STSchemeColorVal.accent2],
                },
                accent3: {
                    val: STSchemeColorVal.accent3,
                    color: clrScheme[STSchemeColorVal.accent3],
                },
                accent4: {
                    val: STSchemeColorVal.accent4,
                    color: clrScheme[STSchemeColorVal.accent4],
                },
                accent5: {
                    val: STSchemeColorVal.accent5,
                    color: clrScheme[STSchemeColorVal.accent5],
                },
                accent6: {
                    val: STSchemeColorVal.accent6,
                    color: clrScheme[STSchemeColorVal.accent6],
                },
                hlink: {
                    val: STSchemeColorVal.hlink,
                    color: clrScheme[STSchemeColorVal.hlink],
                },
                folHlink: {
                    val: STSchemeColorVal.folHlink,
                    color: clrScheme[STSchemeColorVal.folHlink],
                },
            };

            const newColorSchema = Object.assign(localColorSchema, colorSchemaProps);
            const props: IThemeSelectItem = {
                id: id || '',
                colorSchema: newColorSchema,
            };

            themeModel.updateTheme(props);
        });
    }
}
